import React from 'react';

import { Box, Typography, Grid, Button } from '@material-ui/core';
import clsx from 'clsx';

import { DetailItem } from '@vyce/core/src/types';
import { getFormattedDate } from '@vyce/core/src/utils';
import { IconStatusComponent } from '@vyce/core/src/components/Statuses/IconStatusComponent';
import { RTW_VALUES, getRtwStatusLabel } from '@vyce/core/src/constants';

import useStyles from '../../styles';
import { BoxWrapper } from './BoxWrapper';

interface RightToWorkProps {
  rtw: DetailItem & { expiration_date?: string | null };
  id: DetailItem;
  share_code: DetailItem;
  showViewDocumentButton: boolean;
  onOpenIdInfoDialog: () => void;
}

const expStatuses = ['Expiring', 'Expired'];

const RTW_NEUTRAL_STATUSES: string[] = [RTW_VALUES.INDEFINITE, RTW_VALUES.UNVERIFIED];
const RTW_WARNING_STATUSES: string[] = [RTW_VALUES.LIMITED, RTW_VALUES.EXPIRING];

const RtwDataRowItem = ({
  title,
  value,
  verified,
  expiration_date,
}: DetailItem & { expiration_date?: string | null }) => {
  const classes = useStyles();

  if (!value) return null;
  return (
    <Box className={classes.recordWrapper} marginBottom={2}>
      <Grid item xs={12} sm={6} lg={5}>
        <Box display="flex" alignItems="center" height="100%">
          <Typography className={classes.bold}>{title}:</Typography>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6} lg={7}>
        <Box display="flex" alignItems="start" justifyContent="space-between">
          <Box display="flex" flexDirection="column">
            <Typography
              color={
                RTW_NEUTRAL_STATUSES.includes(value) || RTW_WARNING_STATUSES.includes(value)
                  ? 'textSecondary'
                  : 'error'
              }
              className={clsx(classes.bigTextWrap, {
                [classes.warningText]: RTW_WARNING_STATUSES.includes(value),
              })}
            >
              {getRtwStatusLabel(value)}
            </Typography>
            {expiration_date && (
              <Typography color="textSecondary" className={classes.bigTextWrap}>
                Exp. {getFormattedDate(expiration_date)}
              </Typography>
            )}
          </Box>

          <IconStatusComponent verified={verified} expiry_date={expiration_date} />
        </Box>
      </Grid>
    </Box>
  );
};

const DataRowItem = ({
  title,
  value,
  verified,
  expiration_date,
  showStatus = true,
}: DetailItem & { expiration_date?: string; showStatus?: boolean }) => {
  const classes = useStyles();

  if (!value) return null;
  return (
    <Box className={classes.recordWrapper} marginBottom={2}>
      <Grid item xs={12} sm={6} lg={5}>
        <Box display="flex" alignItems="center" height="100%">
          <Typography className={classes.bold}>{title}:</Typography>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6} lg={7}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" flexDirection="column">
            <Typography color={verified ? 'textSecondary' : 'error'} className={classes.bigTextWrap}>
              {value === 'Vyce verified' && verified ? 'Verified' : value}
            </Typography>
            {expiration_date && (
              <Typography color="textSecondary" className={classes.bigTextWrap}>
                {expStatuses.includes(value) ? '' : 'Exp.'} {getFormattedDate(expiration_date)}
              </Typography>
            )}
          </Box>
          {showStatus && <IconStatusComponent verified={verified} expiry_date={expiration_date} />}
        </Box>
      </Grid>
    </Box>
  );
};

export const RightToWork = ({
  rtw,
  id,
  share_code,
  showViewDocumentButton,
  onOpenIdInfoDialog,
}: RightToWorkProps) => {
  const classes = useStyles();

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Typography color="textSecondary" variant="h6">
        ID & Right to Work
      </Typography>
      <BoxWrapper>
        <Box flex="1 0 auto" width="100%">
          <RtwDataRowItem {...rtw} />
          <DataRowItem {...share_code} />
          <DataRowItem {...id} />
        </Box>

        {showViewDocumentButton && (
          <Box width="100%">
            <Button
              color="primary"
              variant="outlined"
              size="small"
              className={classes.outlinedIDButton}
              onClick={onOpenIdInfoDialog}
            >
              View Documents
            </Button>
          </Box>
        )}
      </BoxWrapper>
    </Box>
  );
};
